import { Injectable } from '@angular/core';
import { Supplier } from 'src/app/model/supplier';
import { UserService } from '../user/user.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, interval } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SupplierInterface } from 'src/app/model/supplier-interface';
import { ToastrService } from 'ngx-toastr';
import { AppConstants } from 'src/app/constants/app-constants';

/**
 * SupplierService is used to hold/move supplier data from the API to the website
 */
@Injectable({
  providedIn: 'root'
})
export class SupplierService {
  /** A list of suppliers the user has access to */
  private _suppliers: Supplier[] = [];
  private _suppliersSubject = new Subject<Supplier[]>();
  public suppliers$ = this._suppliersSubject.asObservable();

  /** An observable to refresh API data */
  public timer: Observable<number> = interval(AppConstants.API_FETCH_INTERVAL);
  private _timers: number[] = [0];

  /** How long it has been since the last refresh */
  private _timeSinceLastRefresh = new BehaviorSubject<string>("0:00");
  public timeSinceLastRefresh$ = this._timeSinceLastRefresh.asObservable();

  /**
   * Default constructor for the supplier service, used to initialize services
   * 
   * @param http
   * @param authService 
   */
  constructor(
    private _http: HttpClient,
    private _userService: UserService,
    private _toastrService: ToastrService,
  ) {
    // Get the suppliers when the token first loads
    this._userService.token$.subscribe(
      (token: string) => {
        if (token !== "") this._fetchSuppliers();
      });

    // Continuously query the backend to receive new suppliers
    this._startTimer(0);
    this.timer.subscribe(
      (x: number) => {
        this._timers.splice(this._timers.indexOf(x));
        this._startTimer(x + 1);
        this._fetchSuppliers();
      });
  }

  /**
   * Get a list of suppliers
   * 
   * @return A reference to the current suppliers
   */
  public getSuppliers(): Supplier[] {
    return this._suppliers;
  }

  /**
   * Get a supplier by merchant id. Returns undefined if not found
   * 
   * @param merchantID The id of the supplier to get
   * @return The supplier with the given id
   */
  public getSupplierByMerchantId(merchantID: string): Supplier | undefined {
    for (let i = 0; i < this._suppliers.length; i++) {
      if (this._suppliers[i].merchantID === merchantID) {
        return this._suppliers[i];
      }
    }

    return undefined;
  }

  /**
   * Set the suppliers to a given array
   * 
   * @param suppliers
   */
  private _setSuppliers(suppliers: Supplier[]): void {
    this._suppliers = suppliers.map(x => x.copyOf());
    this._suppliersSubject.next(suppliers.map(x => x.copyOf()));
  }

  /**
   * Update a supplier
   * 
   * @param id The id of the supplier to update
   * @param intf The supplier to update
   */
  public updateSupplier(id: number, intf: SupplierInterface): void {
    intf.id = id; // Update the id of the interface for addition to the list of suppliers later

    this._http.put(environment.baseUrl + `/suppliers/${id}`, intf).subscribe({
      next: (id: any): void => {
        // Update the subject to have the new supplier on success
        const old: Supplier[] = this._suppliers;
        for (let i = 0; i < old.length; i++) {
          if (old[i].id === id) {
            old[i] = new Supplier(intf);
            break;
          }
        }

        this._setSuppliers(old);
        this._toastrService.success("Successfully updated supplier!");
      },
    });
  }

  /**
   * Create a new supplier
   * 
   * @param intf the supplier to create 
   */
  public createSupplier(intf: SupplierInterface): void {
    this._http.post(environment.baseUrl + `/suppliers`, intf).subscribe({
      next: (id: any): void => {
        intf.id = id as number;

        // Update the subject to have the new supplier on success
        const old: Supplier[] = this._suppliers;
        old.push(new Supplier(intf));

        this._setSuppliers(old);
        this._toastrService.success("Successfully created supplier!");
      },
    });
  }

  /**
   * Delete a new supplier
   * 
   * @param intf the supplier to create 
   */
  public deleteSupplier(intf: SupplierInterface): void {
    this._http.delete(environment.baseUrl + `/suppliers/${intf.id}`).subscribe({
      next: (_: any): void => {
        // Update the subject to have the new supplier on success
        this._setSuppliers(this._suppliers.filter(x => x.id !== intf.id));
        this._toastrService.success("Successfully deleted supplier!");
      },
    });
  }

  /**
   * Get applications from the backend. You should subscribe to the
   * 'suppliers$' field to receive updates
   */
  private _fetchSuppliers(): void {
    this._http.get(environment.baseUrl + "/suppliers").subscribe({
      next: (data: Object): void => {
        // Cast the data into a list of application interfaces
        const supplierInterfaces = data as SupplierInterface[];
        if (supplierInterfaces == undefined) {
          console.error(`[ERR]: cannot cast backend output into supplierInterface list`);
        }

        // Convert the interfaces to class values
        const suppliers: Supplier[] = [];
        for (let intf of supplierInterfaces) {
          suppliers.push(new Supplier(intf));
        }

        // Propagate changes if different
        const current = this._suppliers;
        if (!arraysEqual(current, suppliers)) {
          this._setSuppliers(suppliers);
          console.log(`[LOG]: successfully queried suppliers`);
        }
      },
    });
  }

  /**
   * Start the last updated time from 0
   */
  private async _startTimer(index: number) {
    this._timers.push(index);

    let seconds = 0;
    let minutes = 0;

    while (this._timers.includes(index)) {
      // Increment seconds
      await delay(1000);
      seconds++;

      // Increment minutes
      if (seconds >= 60) {
        seconds = 0;
        minutes++;
      }

      // Update timer
      this._timeSinceLastRefresh.next(`${minutes}:${(""+seconds).length === 1 ? "0" : ""}${seconds}`);
    }
  }
}

const arraysEqual = (a: Supplier[], b: Supplier[]): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  for (var i = 0; i < a.length; ++i) {
    if ((a[i] === null && a[i] !== b[i]) || !a[i].equals(b[i])) return false;
  }
  return true;
}

const delay = (delay: number) => {
  return new Promise(r => {setTimeout(r, delay)});
}